import BigNumber from 'bignumber.js'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { BIG_ZERO } from 'utils/bigNumber'
import { Flex, Text, Box } from '@pixel-nft/uikit'
import { useTranslation } from 'contexts/Localization'
import web3 from 'utils/web3'
import { getPxfTokenAddress, getUSDTAddress } from 'utils/addressHelpers'
import { convertLockPeriod } from 'utils/dateHandle'
import { PoolCategory } from 'config/constants/types'
import { LendingPool, Pool } from 'state/types'
import ApprovalAction from './ApprovalAction'
import StakeActions from './StakeActions'
import WithdrawActions from './WithdrawActions'
import ClaimActions from './ClaimActions'

const InlineText = styled(Text)`
  display: inline;
  font-size: 14px;
`

const CardActions: React.FC<{ pool: LendingPool; userData: any; userPoolData: any }> = ({
  pool,
  userData,
  userPoolData,
}) => {
  const pxfAllowance = userData?.pxfAllowance ? new BigNumber(userData.pxfAllowance) : BIG_ZERO
  const pxfBalance = userData?.pxfBalance ? new BigNumber(userData.pxfBalance) : BIG_ZERO
  const usdtAllowance = userData?.usdtAllowance ? new BigNumber(userData.usdtAllowance) : BIG_ZERO
  const usdtBalance = userData?.usdtBalance ? new BigNumber(userData.usdtBalance) : BIG_ZERO

  const hasEnoughPXF = userPoolData?.hasEnoughPXF ? userPoolData.hasEnoughPXF : false
  const calculateReward = userPoolData?.calculateReward ? new BigNumber(userPoolData.calculateReward) : BIG_ZERO
  const pxfDeposit = userPoolData?.pxfDeposit ? userPoolData.pxfDeposit : BIG_ZERO
  const poolIsRunning = userPoolData?.poolIsRunning ? userPoolData.poolIsRunning : false

  const poolInfos = userPoolData?.infos ? userPoolData.infos : []

  // Pools using native BNB behave differently than pools using a token
  const needsPxfApproval = !pxfAllowance.gte(new BigNumber(pool.minimumPXF))
  const needsUsdtApproval = !usdtAllowance.gt(0)
  const isLoading = !userData
  const [curBlockTimestamp, setCurBlockTimestamp] = useState(0)

  useEffect(() => {
    const patchCurTimestamp = async () => {
      const currentTime = Math.floor(Date.now() / 1000)
      setCurBlockTimestamp(currentTime)
    }
    if (poolIsRunning) {
      patchCurTimestamp()
      const intervalId = setInterval(patchCurTimestamp, 1000) // 毎秒更新
      return () => clearInterval(intervalId) // クリーンアップ
    }
    return null
  }, [poolIsRunning])

  return (
    <Flex flexDirection="column">
      <Flex flexDirection="column" my="12px">
        {!poolIsRunning && (
          <>
            {needsPxfApproval ? (
              <ApprovalAction tokenAddress={getPxfTokenAddress()} isLoading={isLoading} />
            ) : (
              <>
                {!hasEnoughPXF && (
                  <StakeActions isPxf poolId={pool.poolId} stakingTokenBalance={pxfBalance} isLoading={isLoading} />
                )}

                {new BigNumber(pxfDeposit).gt(0) && (
                  <WithdrawActions
                    isPxf
                    poolId={pool.poolId}
                    totalAmount={pxfDeposit}
                    isLoading={isLoading}
                    label="PXF Withdraw"
                  />
                )}
              </>
            )}
          </>
        )}

        <Box display="inline" mt="8px">
          <Flex alignItems="center">
            <InlineText>USDT</InlineText>
            <Text color="textSubtle" fontSize="14px">
              &nbsp;Reward
            </Text>
          </Flex>
        </Box>
        <ClaimActions poolId={pool.poolId} earnings={calculateReward} isLoading={isLoading} />

        {poolIsRunning && (
          <>
            <Box display="inline" mb="8px">
              <Flex alignItems="center">
                <InlineText>USDT</InlineText>
                <Text color="textSubtle" fontSize="14px">
                  &nbsp;Lending Slot
                </Text>
              </Flex>
            </Box>

            {poolInfos
              .filter((info) => info.isRunning)
              .map((info) => (
                <WithdrawActions
                  key={`${info.poolId}-${info.slotId}`}
                  poolId={pool.poolId}
                  slotId={info.slotId}
                  totalAmount={info.depositAmount}
                  isLoading={isLoading}
                  label={
                    !info.canWithdraw && parseInt(info.depositTime) + parseInt(pool.lockPeriod) - curBlockTimestamp >= 0
                      ? convertLockPeriod(parseInt(info.depositTime) + parseInt(pool.lockPeriod) - curBlockTimestamp)
                      : 'USDT Withdraw'
                  }
                  disabled={!info.canWithdraw}
                />
              ))}
          </>
        )}

        {hasEnoughPXF && (
          <>
            <Box display="inline" mb="8px">
              <Flex alignItems="center">
                <InlineText>USDT</InlineText>
                <Text color="textSubtle" fontSize="14px">
                  &nbsp;New Deposit
                </Text>
              </Flex>
            </Box>

            <Box display="inline" mb="8px">
              {needsUsdtApproval ? (
                <ApprovalAction tokenAddress={getUSDTAddress()} isLoading={isLoading} />
              ) : (
                <StakeActions
                  poolId={pool.poolId}
                  stakingTokenBalance={usdtBalance}
                  isLoading={isLoading}
                  buttonDisabled={false}
                />
              )}
            </Box>
          </>
        )}
      </Flex>
    </Flex>
  )
}

export default CardActions
